import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { PageHero, SectionLabel } from "@/components/site/Sections";
import { Phone, Mail, MapPin, Clock, ArrowUpRight, MessageCircle } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT, FAQS } from "@/lib/data";

const Contact = () => (
  <div data-testid="contact-page">
    <PageHero
      label="Contact / Enquiry"
      title="Let's discuss your requirements."
      subtitle="Share your paper or board requirement and we'll respond quickly with honest advice on grade, availability and value."
    />

    <section className="relative z-10 pb-24 lg:pb-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Contact details */}
        <div className="lg:col-span-5">
          <Reveal><SectionLabel>Get in touch</SectionLabel></Reveal>
          <Stagger className="mt-8 space-y-px bg-paper-border border border-paper-border rounded-sm overflow-hidden">
            <StaggerItem className="bg-paper p-7 flex items-start gap-4">
              <MapPin className="h-6 w-6 text-forest shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Visit us</p>
                <p className="mt-2 font-medium text-forest">{CONTACT.company}</p>
                <p className="text-ink-soft leading-relaxed">{CONTACT.address.join(" ")}</p>
              </div>
            </StaggerItem>
            <StaggerItem className="bg-paper p-7 flex items-start gap-4">
              <Phone className="h-6 w-6 text-forest shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Call us</p>
                <a href={`tel:${CONTACT.phoneMobileRaw}`} data-testid="contact-mobile" className="mt-2 block font-medium text-forest link-underline w-fit">{CONTACT.phoneMobile} · {CONTACT.person}</a>
                <a href={`tel:${CONTACT.phoneLineRaw}`} data-testid="contact-landline" className="block text-ink-soft link-underline w-fit">{CONTACT.phoneLine}</a>
              </div>
            </StaggerItem>
            <StaggerItem className="bg-paper p-7 flex items-start gap-4">
              <Mail className="h-6 w-6 text-forest shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Email us</p>
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="mt-2 block font-medium text-forest link-underline w-fit break-all">{CONTACT.email}</a>
              </div>
            </StaggerItem>
            <StaggerItem className="bg-paper p-7 flex items-start gap-4">
              <Clock className="h-6 w-6 text-forest shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Business hours</p>
                <p className="mt-2 font-medium text-forest">{CONTACT.hours}</p>
              </div>
            </StaggerItem>
          </Stagger>
        </div>

        {/* CTA + map */}
        <div className="lg:col-span-7">
          <Reveal>
            <div className="rounded-sm bg-forest text-paper p-9 lg:p-12">
              <h2 className="font-serif text-3xl lg:text-4xl tracking-tight leading-tight">
                The fastest way to reach us.
              </h2>
              <p className="mt-4 text-paper/70 leading-relaxed">
                Message us on WhatsApp for quick answers on products, bulk orders and availability —
                or call directly. We&rsquo;re here to help you source with confidence.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-whatsapp-cta"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-paper px-7 py-4 font-semibold text-forest transition-colors duration-300 hover:bg-paper-soft"
                >
                  <MessageCircle className="h-5 w-5" /> Chat on WhatsApp
                </a>
                <a
                  href={`mailto:${CONTACT.email}?subject=Paper%20%26%20Board%20Enquiry`}
                  data-testid="contact-email-cta"
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-paper/30 px-7 py-4 font-semibold text-paper transition-colors duration-300 hover:bg-paper/10"
                >
                  Email an Enquiry
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="mt-8 overflow-hidden rounded-sm border border-paper-border">
            <iframe
              title="Mysore Paper location map"
              data-testid="contact-map"
              src="https://www.google.com/maps?q=Sultanpet%20Circle%2C%20Bangalore%20560053&output=embed"
              className="w-full h-[320px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section data-testid="faq-section" className="relative z-10 py-20 lg:py-28 bg-paper-soft/50 border-y border-paper-border">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="mb-12 text-center">
          <SectionLabel>Frequently asked</SectionLabel>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-forest leading-tight">
            Questions from trade buyers.
          </h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-paper-border">
              <AccordionTrigger className="text-left font-serif text-xl lg:text-2xl text-forest hover:no-underline py-6">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-base text-ink-soft leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  </div>
);

export default Contact;
